import React, { useEffect, useMemo } from 'react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import RequireDesktopOrIpad from '../components/RequireDesktopOrIpad';
import { useIsMobile } from '../hooks/useIsMobile';
import { PRODUCTION_PATHS, resolveHostedAppUrl } from '../config/siteUrl';
import { auth } from '../services/firebase';

const REDROCK_BOOTSTRAP_TOKEN_KEY = 'cognidoor.redrock.bootstrap.firebase_token';

function getHostedRedRockNextPath(baseUrl, currentSuffix = '', search = '') {
  try {
    const url = new URL(baseUrl);
    const basePath = url.pathname && url.pathname !== '/' ? url.pathname.replace(/\/$/, '') : '';
    return `${basePath}${currentSuffix}${search}` || '/';
  } catch {
    return `/redrock-app${currentSuffix}${search}`;
  }
}

/**
 * Red Rock Simulator launcher.
 * Redirects to the actual Red Rock app.
 * - On localhost: uses http://localhost:8090 (dev proxy target).
 * - Else (Vercel preview, etc.): uses production URL so we never send users to localhost.
 * - Override with REACT_APP_REDROCK_APP_URL if needed.
 * - On mobile: show "requires computer or iPad" message instead of redirecting.
 */
function getRedRockAppUrl() {
  return resolveHostedAppUrl({
    envName: 'REACT_APP_REDROCK_APP_URL',
    localhostUrl: 'http://localhost:8090',
    productionPath: PRODUCTION_PATHS.redrockApp,
  });
}

export default function RedRockAppPage() {
  const isMobile = useIsMobile(767);
  const baseUrl = useMemo(getRedRockAppUrl, []);

  useEffect(() => {
    if (isMobile) return;

    async function handoff() {
      const currentPath = window.location.pathname.replace(/^\/redrock-app/, '') || '';
      const nextPath = getHostedRedRockNextPath(baseUrl, currentPath, window.location.search);
      const target = `${baseUrl.replace(/\/$/, '')}/auth/bootstrap?next=${encodeURIComponent(nextPath)}`;

      try {
        const currentUser = auth.currentUser;
        if (currentUser?.getIdToken) {
          const token = await currentUser.getIdToken();
          window.sessionStorage.setItem(REDROCK_BOOTSTRAP_TOKEN_KEY, token);
        } else {
          window.sessionStorage.removeItem(REDROCK_BOOTSTRAP_TOKEN_KEY);
        }
      } catch {
        window.sessionStorage.removeItem(REDROCK_BOOTSTRAP_TOKEN_KEY);
      }

      window.location.assign(target);
    }

    void handoff();
  }, [baseUrl, isMobile]);

  if (isMobile) {
    return <RequireDesktopOrIpad backTo="/redrock" backLabel="View Red Rock simulator" />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <LoadingSpinner tone="dark" size="md" className="shrink-0" />
        <p className="text-sm font-medium text-white/90">Opening Red Rock Simulator…</p>
        <p className="text-xs text-white/60">If you are not redirected, open the app directly.</p>
        <a
          href={`${baseUrl.replace(/\/$/, '')}/auth/bootstrap?next=${encodeURIComponent(getHostedRedRockNextPath(baseUrl))}`}
          className="text-sm font-semibold text-sky-300 hover:text-sky-200 underline underline-offset-2"
        >
          Open Red Rock Simulator
        </a>
      </div>
    </div>
  );
}
